import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, Modal, message, Tag, Icon, Form, Input, InputNumber, Radio, DatePicker, Select, List, Avatar, Spin, Checkbox } from 'antd';
import moment from 'moment';
import {
    getPointActivityListWithActive,
    getPointActivityListWithArchived,
    archivePointActivity,
    unarchivePointActivity,
    deletePointActivity,
    addPointActivity,
    togglePointActivityEnabled,
    assignTasks
} from '@/api/points';
import { getUserListWithPagination } from '@/api/user';
import './index.less';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PointActivity = (props) => {
    const [activeList, setActiveList] = useState([]);
    const [archivedList, setArchivedList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState('task');
    const [pointTypeFilter, setPointTypeFilter] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [archivedPagination, setArchivedPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);
    const [userModalVisible, setUserModalVisible] = useState(false);
    const [userSearchText, setUserSearchText] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userLoading, setUserLoading] = useState(false);
    const [userLoadingMore, setUserLoadingMore] = useState(false);
    const [userPagination, setUserPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0,
        hasMore: false
    });
    const [assignDate, setAssignDate] = useState(null);

    // 获取未归档列表
    const fetchActiveList = async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const params = {
                page,
                pageSize
            };
            // 只有当选择了筛选条件时才添加 pointType 参数
            if (pointTypeFilter) {
                params.pointType = pointTypeFilter;
            }
            const response = await getPointActivityListWithActive(params);
            setActiveList(response.result.activities || []);
            setPagination({
                ...pagination,
                current: page,
                pageSize,
                total: response.result.total
            });
        } catch (error) {
            message.error('获取未归档活动列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 获取已归档列表
    const fetchArchivedList = async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const params = {
                page,
                pageSize
            };
            // 只有当选择了筛选条件时才添加 pointType 参数
            if (pointTypeFilter) {
                params.pointType = pointTypeFilter;
            }
            const response = await getPointActivityListWithArchived(params);
            setArchivedList(response.result.activities || []);
            setArchivedPagination({
                ...archivedPagination,
                current: page,
                pageSize,
                total: response.result.total
            });
        } catch (error) {
            message.error('获取已归档活动列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 获取用户列表
    const fetchUserList = async () => {
        try {
            const response = await getUserListWithPagination();
            if (response.success) {
                const options = response.result.map(user => ({
                    label: user.user_name || user.phone,
                    value: user._id
                }));
                setUserOptions(options);
            }
        } catch (error) {
            message.error('获取用户列表失败');
        }
    };

    // 处理任务分配
    const handleAssignTasks = async () => {
        if (!selectedUsers.length) {
            message.warning('请选择要分配给的用户');
            return;
        }
        if (!selectedTasks.length) {
            message.warning('请选择要分配的任务');
            return;
        }

        try {
            setAssignLoading(true);
            const response = await assignTasks({
                userIds: selectedUsers.map(u => u.id),
                activityIds: selectedTasks
            });

            if (response.success) {
                message.success('任务分配成功');
                // 显示分配结果统计
                Modal.success({
                    title: '任务分配完成',
                    content: (
                        <div>
                            <p>用户处理结果：</p>
                            <p>总数：{response.result.summary.users.total}</p>
                            <p>成功：{response.result.summary.users.success}</p>
                            <p>失败：{response.result.summary.users.error}</p>
                            <p>活动处理结果：</p>
                            <p>总数：{response.result.summary.activities.total}</p>
                            <p>成功：{response.result.summary.activities.success}</p>
                            <p>跳过：{response.result.summary.activities.skipped}</p>
                            <p>失败：{response.result.summary.activities.error}</p>
                        </div>
                    )
                });
                setAssignModalVisible(false);
                setSelectedUsers([]);
                setSelectedTasks([]);
            } else {
                message.error(response.message || '任务分配失败');
            }
        } catch (error) {
            message.error('任务分配失败：' + error.message);
        } finally {
            setAssignLoading(false);
        }
    };

    // 处理分配给用户
    const handleAssignToUsers = async () => {
        if (!selectedTasks.length) {
            message.warning('请选择要分配的任务');
            return;
        }
        if (!selectedUsers.length) {
            message.warning('请选择要分配给的用户');
            return;
        }

        setAssignLoading(true);
        try {
            const result = await assignTasks({
                activityIds: selectedTasks,
                userIds: selectedUsers.map(u => u.id),
                assignDate: assignDate || moment().format('YYYY-MM-DD')
            });

            // 显示分配结果统计
            Modal.success({
                title: '任务分配完成',
                content: (
                    <div>
                        <p>分配结果统计：</p>
                        <p>成功：{result.result.summary.success} 个</p>
                        <p>跳过：{result.result.summary.skipped} 个</p>
                        <p>失败：{result.result.summary.error} 个</p>
                        <p>总计：{result.result.summary.total} 个</p>
                    </div>
                )
            });

            setUserModalVisible(false);
            setSelectedUsers([]);
            setSelectedTasks([]);
            setAssignDate(null);
            // 刷新任务列表
            fetchActiveList();
        } catch (error) {
            console.error('分配任务失败:', error);
            Modal.error({
                title: '任务分配失败',
                content: error.message || '请稍后重试'
            });
        } finally {
            setAssignLoading(false);
        }
    };

    // 处理积分类型筛选变化
    const handlePointTypeChange = (value) => {
        setPointTypeFilter(value);
        // 重置分页
        setPagination(prev => ({ ...prev, current: 1 }));
        setArchivedPagination(prev => ({ ...prev, current: 1 }));
    };

    // 监听筛选条件变化
    useEffect(() => {
        fetchActiveList(1, pagination.pageSize);
        fetchArchivedList(1, archivedPagination.pageSize);
    }, [pointTypeFilter]); // 当 pointTypeFilter 变化时重新获取数据

    // 初始化加载
    useEffect(() => {
        fetchActiveList();
        fetchArchivedList();
    }, []); // 仅在组件挂载时执行一次

    // 在组件挂载时获取用户列表
    useEffect(() => {
        fetchUserList();
    }, []);

    // 处理分页变化
    const handleTableChange = async (paginationConfig, _, __, isArchived = false) => {
        const { current, pageSize } = paginationConfig;
        if (isArchived) {
            await fetchArchivedList(current, pageSize);
        } else {
            await fetchActiveList(current, pageSize);
        }
    };

    // 归档活动
    const handleArchive = async (record) => {
        Modal.confirm({
            title: '确认归档',
            content: '确定要归档这个积分活动吗？归档后将不能被用户使用。',
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await archivePointActivity(record._id);
                    message.success('归档成功');
                    fetchActiveList();
                    fetchArchivedList();
                } catch (error) {
                    message.error('归档失败');
                }
            }
        });
    };

    // 恢复归档活动
    const handleUnarchive = async (record) => {
        try {
            await unarchivePointActivity(record._id);
            message.success('恢复成功');
            fetchActiveList();
            fetchArchivedList();
        } catch (error) {
            message.error('恢复失败');
        }
    };

    // 删除活动
    const handleDelete = async (record) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个积分活动吗？',
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await deletePointActivity(record._id);
                    if (res.success) {
                        message.success(res.message || '删除成功');
                        fetchActiveList();
                        fetchArchivedList();
                    } else {
                        message.error(res.message || '删除失败');
                    }
                } catch (error) {
                    message.error(error.message || '删除失败');
                }
            }
        });
    };

    // 处理启用/禁用
    const handleToggleEnabled = async (record) => {
        try {
            const newStatus = !record.isEnabled;
            await togglePointActivityEnabled({
                activityId: record._id,
                enabled: newStatus
            });
            message.success(`${newStatus ? '启用' : '禁用'}成功`);
            fetchActiveList();
            fetchArchivedList();
        } catch (error) {
            message.error(`操作失败: ${error.message}`);
        }
    };

    // 打开添加模态框
    const showAddModal = (type) => {
        setModalType(type);
        setIsModalVisible(true);
        props.form.resetFields();
    };

    // 关闭添加模态框
    const handleCancel = () => {
        setIsModalVisible(false);
        props.form.resetFields();
    };

    // 处理添加活动
    const handleAddActivity = () => {
        props.form.validateFields(async (err, values) => {
            if (!err) {
                try {
                    // 构造活动数据
                    const activityData = {
                        name: values.name,
                        points: values.points,
                        pointType: modalType === 'task' ? 'reward' : 'consume',
                        activityType: values.activityType,
                        description: values.description,
                        isEnabled: true
                    };

                    // 如果是日常任务，添加日程类型
                    if (values.activityType === 'daily' && modalType === 'task') {
                        activityData.scheduleType = values.scheduleType;
                    }

                    // 如果是特定活动，添加时间范围
                    if (values.activityType === 'special' && values.timeRange) {
                        activityData.timeRange = {
                            startTime: values.timeRange[0].toDate(),
                            endTime: values.timeRange[1].toDate()
                        };
                    }

                    const response = await addPointActivity({ activityData });
                    if (response.result) {
                        message.success(modalType === 'task' ? '添加积分任务成功' : '添加积分活动成功');
                        setIsModalVisible(false);
                        props.form.resetFields();
                        // 刷新活动列表
                        fetchActiveList();
                    } else {
                        message.error(response.message || '添加失败');
                    }
                } catch (error) {
                    message.error(modalType === 'task' ? '添加积分任务失败' : '添加积分活动失败');
                }
            }
        });
    };

    const columns = [
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: '类型',
            dataIndex: 'pointType',
            key: 'pointType',
            width: 100,
            render: (text) => (
                <Tag color={text === 'reward' ? 'blue' : 'orange'}>
                    {text === 'reward' ? '积分任务' : '积分活动'}
                </Tag>
            ),
        },
        {
            title: '积分值',
            dataIndex: 'points',
            key: 'points',
            width: 100,
            render: (points, record) => (
                <span style={{ 
                    color: record.pointType === 'reward' ? '#52c41a' : '#f5222d',
                    fontSize: '14px',
                    fontWeight: 500,
                    display: 'inline-block',
                    minWidth: '60px'
                }}>
                    {record.pointType === 'reward' ? '+' : '-'}{points}
                </span>
            ),
        },
        {
            title: '活动类型',
            dataIndex: 'activityType',
            key: 'activityType',
            width: 100,
            render: (text) => (
                <Tag color={text === 'daily' ? 'green' : 'purple'}>
                    {text === 'daily' ? '日常' : '特定'}
                </Tag>
            ),
        },
        {
            title: '时间范围',
            key: 'timeRange',
            width: 200,
            render: (_, record) => {
                if (record.activityType === 'special' && record.timeRange) {
                    return `${moment(record.timeRange.startTime).format('YYYY-MM-DD')} 至 ${moment(record.timeRange.endTime).format('YYYY-MM-DD')}`;
                }
                return '长期有效';
            },
        },
        {
            title: '状态',
            width: 120,
            render: (_, record) => (
                <div className="status-tags">
                    <Tag color={record.isEnabled ? 'green' : 'red'}>
                        {record.isEnabled ? '启用' : '禁用'}
                    </Tag>
                    <Tag color={record.status === 'active' ? 'blue' : 'orange'}>
                        {record.status === 'active' ? '活跃' : '已归档'}
                    </Tag>
                </div>
            ),
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 200,
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '更新时间',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 150,
            render: (text) => moment(text).format('YYYY-MM-DD HH:mm'),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 200,
            render: (_, record) => (
                <div className="action-buttons">
                    {record.status === 'active' ? (
                        <>
                            <Button 
                                type="link" 
                                onClick={() => handleToggleEnabled(record)}
                                style={{ color: record.isEnabled ? '#ff4d4f' : '#52c41a' }}
                            >
                                {record.isEnabled ? '禁用' : '启用'}
                            </Button>
                            <Button type="link" onClick={() => handleArchive(record)}>
                                归档
                            </Button>
                            <Button type="link" onClick={() => handleDelete(record)}>
                                删除
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button type="link" onClick={() => handleUnarchive(record)}>
                                恢复
                            </Button>
                            <Button type="link" onClick={() => handleDelete(record)}>
                                删除
                            </Button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    const { getFieldDecorator, getFieldValue } = props.form;
    const activityType = getFieldValue('activityType');

    const rowSelection = {
        selectedRowKeys: selectedTasks,
        onChange: (selectedRowKeys) => {
            setSelectedTasks(selectedRowKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: !record.isEnabled || record.status !== 'active',
            name: record.name,
        }),
    };

    // 修改表格的extra部分
    const tableExtra = (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {selectedTasks.length > 0 && (
                <Button 
                    type="primary" 
                    onClick={() => {
                        setUserModalVisible(true);
                        // 打开Modal时加载用户列表，并清空之前的选择
                        loadUsers('', 1, false);
                        setSelectedUsers([]);
                        setUserSearchText('');
                        setUserSearchResults([]);
                        setUserPagination({
                            page: 1,
                            limit: 10,
                            total: 0,
                            pages: 0,
                            hasMore: false
                        });
                    }}
                >
                    分配给用户 ({selectedTasks.length})
                </Button>
            )}
            <Button type="primary" onClick={() => showAddModal('task')}>
                <Icon type="plus" />添加积分任务
            </Button>
            <Button type="primary" onClick={() => showAddModal('activity')}>
                <Icon type="plus" />添加积分活动
            </Button>
        </div>
    );

    // 加载用户数据
    const loadUsers = async (searchValue = '', page = 1, append = false) => {
        if (page === 1) {
            setUserLoading(true);
        } else {
            setUserLoadingMore(true);
        }
        
        try {
            // 构建搜索参数 - 只搜索普通用户
            const searchParams = {
                search_keyword: searchValue,
                user_type: 'user',
                page,
                limit: userPagination.limit,
                role: 'user'
            };

            // 调用分页API进行搜索
            const response = await getUserListWithPagination(searchParams);

            if (response && response.items && response.items.length > 0) {
                // 转换API返回的数据格式
                const formattedResults = response.items.map(user => ({
                    id: user._id,
                    name: user.user_name,
                    gender: user.gender,
                    dob: user.dob ? moment(user.dob).format('YYYY-MM-DD') : '',
                    phone: user.phone ? `${user.phone.substring(0, 3)}****${user.phone.substring(7)}` : '',
                    avatar: user.avatar || ''
                }));

                // 更新分页信息
                setUserPagination({
                    page: response.page,
                    limit: response.limit,
                    total: response.total,
                    pages: response.pages,
                    hasMore: response.has_more
                });

                // 如果是加载更多，则追加数据
                if (append) {
                    setUserSearchResults(prev => [...prev, ...formattedResults]);
                } else {
                    setUserSearchResults(formattedResults);
                }
            } else if (!append) {
                setUserSearchResults([]);
                setUserPagination(prev => ({
                    ...prev,
                    page: 1,
                    total: 0,
                    pages: 0,
                    hasMore: false
                }));
            }
        } catch (error) {
            console.error('获取用户列表失败:', error);
            message.error('获取用户列表失败');
            if (!append) {
                setUserSearchResults([]);
            }
        } finally {
            setUserLoading(false);
            setUserLoadingMore(false);
        }
    };

    // 处理滚动加载更多
    const handleUserScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        // 当滚动到距离底部20px时加载更多
        if (scrollHeight - scrollTop - clientHeight < 20 && userPagination.hasMore && !userLoadingMore && !userLoading) {
            loadUsers(userSearchText, userPagination.page + 1, true);
        }
    };

    // 搜索用户
    const handleUserSearch = (value) => {
        setUserSearchText(value);
        // 重置分页并加载第一页
        setUserPagination(prev => ({
            ...prev,
            page: 1
        }));
        loadUsers(value, 1, false);
    };

    // 选择用户
    const handleUserSelect = (user) => {
        const isSelected = selectedUsers.some(u => u.id === user.id);
        if (isSelected) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    return (
        <div className="point-activity-container">
            <Card 
                title="积分活动与任务管理"
                extra={tableExtra}
            >
                <Modal
                    title={modalType === 'task' ? '添加积分任务' : '添加积分活动'}
                    visible={isModalVisible}
                    onOk={handleAddActivity}
                    onCancel={handleCancel}
                    okText="确定"
                    cancelText="取消"
                    width={600}
                >
                    <Form layout="vertical">
                        <Form.Item label={modalType === 'task' ? '任务名称' : '活动名称'}>
                            {getFieldDecorator('name', {
                                rules: [{ required: true, message: modalType === 'task' ? '请输入任务名称' : '请输入活动名称' }],
                            })(
                                <Input placeholder={modalType === 'task' ? '请输入任务名称' : '请输入活动名称'} />
                            )}
                        </Form.Item>
                        <Form.Item label="活动类型">
                            {getFieldDecorator('activityType', {
                                initialValue: 'daily',
                                rules: [{ required: true, message: '请选择活动类型' }],
                            })(
                                <Radio.Group>
                                    <Radio value="daily">日常{modalType === 'task' ? '任务' : '活动'}</Radio>
                                    <Radio value="special">特定{modalType === 'task' ? '任务' : '活动'}</Radio>
                                </Radio.Group>
                            )}
                        </Form.Item>
                        {activityType === 'daily' && modalType === 'task' && (
                            <Form.Item label="日程类型">
                                {getFieldDecorator('scheduleType', {
                                    rules: [{ required: true, message: '请选择日程类型' }],
                                })(
                                    <Select 
                                        placeholder="请选择日程类型"
                                        getPopupContainer={trigger => trigger.parentNode}
                                    >
                                        <Option value="SLEEP">睡眠</Option>
                                        <Option value="PRESSURE">压力</Option>
                                        <Option value="OXYGEN">血氧</Option>
                                        <Option value="SPORT">运动</Option>
                                        <Option value="BP">血压</Option>
                                        <Option value="WEIGHT">体重</Option>
                                        <Option value="MEDICINE">药物</Option>
                                        <Option value="PT_CN_MASSAGE">中医理疗按摩</Option>
                                        <Option value="PT_CN_AIJIU">艾灸</Option>
                                        <Option value="EXERCISE">体能训练</Option>
                                        <Option value="TRAINING">动作训练</Option>
                                        <Option value="EDUCATION">教育</Option>
                                        <Option value="MEDITATION">冥想</Option>
                                        <Option value="GAME">游戏/认知训练</Option>
                                        <Option value="GOAL">目标</Option>
                                        <Option value="FACE_SCAN">面部扫描</Option>
                                        <Option value="AD_EVALUATION">认知障碍评估</Option>
                                        <Option value="FOLLOW_UP">随访</Option>
                                    </Select>
                                )}
                            </Form.Item>
                        )}
                        <Form.Item label={modalType === 'task' ? '奖励积分' : '所需积分'}>
                            {getFieldDecorator('points', {
                                rules: [{ required: true, message: modalType === 'task' ? '请输入奖励积分' : '请输入所需积分' }],
                            })(
                                <InputNumber 
                                    min={0} 
                                    placeholder={modalType === 'task' ? '请输入奖励积分' : '请输入所需积分'} 
                                    style={{ width: '100%' }} 
                                />
                            )}
                        </Form.Item>
                        {activityType === 'special' && (
                            <Form.Item label="时间范围">
                                {getFieldDecorator('timeRange', {
                                    rules: [{ required: true, message: '请选择时间范围' }],
                                })(
                                    <RangePicker style={{ width: '100%' }} />
                                )}
                            </Form.Item>
                        )}
                        <Form.Item label="描述">
                            {getFieldDecorator('description', {
                                rules: [{ required: true, message: '请输入描述' }],
                            })(
                                <TextArea 
                                    rows={4} 
                                    placeholder={modalType === 'task' ? '请输入任务描述' : '请输入活动描述'} 
                                />
                            )}
                        </Form.Item>
                    </Form>
                </Modal>

                <Modal
                    title="分配任务给用户"
                    visible={assignModalVisible}
                    onOk={handleAssignTasks}
                    onCancel={() => {
                        setAssignModalVisible(false);
                        setSelectedUsers([]);
                    }}
                    confirmLoading={assignLoading}
                    okText="确定分配"
                    cancelText="取消"
                >
                    <Form layout="vertical">
                        <Form.Item label="选择用户">
                            <Select
                                mode="multiple"
                                style={{ width: '100%' }}
                                placeholder="请选择要分配给的用户"
                                value={selectedUsers}
                                onChange={setSelectedUsers}
                                options={userOptions}
                                optionFilterProp="label"
                                showSearch
                            />
                        </Form.Item>
                        <div>
                            已选择 {selectedTasks.length} 个任务
                        </div>
                    </Form>
                </Modal>

                <Modal
                    title="选择用户"
                    visible={userModalVisible}
                    onOk={handleAssignToUsers}
                    onCancel={() => {
                        setUserModalVisible(false);
                        // 关闭时清空所有状态
                        setSelectedUsers([]);
                        setUserSearchText('');
                        setUserSearchResults([]);
                        setUserPagination({
                            page: 1,
                            limit: 10,
                            total: 0,
                            pages: 0,
                            hasMore: false
                        });
                    }}
                    width={600}
                    confirmLoading={assignLoading}
                >
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ 
                            padding: '12px 16px',
                            background: '#f5f5f5',
                            borderRadius: '4px',
                            marginBottom: '16px'
                        }}>
                            <div style={{ marginBottom: 8 }}>
                                <span style={{ fontWeight: 500 }}>已选择的活动：</span>
                                <span style={{ color: '#1890ff' }}>{selectedTasks.length}</span> 个
                            </div>
                            <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.45)' }}>
                                {activeList.filter(item => selectedTasks.includes(item._id)).map(item => (
                                    <Tag 
                                        key={item._id} 
                                        style={{ marginBottom: 4 }}
                                        color={item.pointType === 'reward' ? 'blue' : 'orange'}
                                    >
                                        {item.name} ({item.pointType === 'reward' ? '+' : '-'}{item.points}积分)
                                    </Tag>
                                ))}
                            </div>
                        </div>
                        <Input.Search
                            placeholder="搜索用户（可搜索用户名或手机号）"
                            onSearch={handleUserSearch}
                            style={{ width: '100%' }}
                            loading={userLoading}
                            value={userSearchText}
                            onChange={(e) => {
                                const value = e.target.value;
                                setUserSearchText(value);
                                // 当搜索框内容变化时就触发搜索
                                handleUserSearch(value);
                            }}
                            allowClear
                        />
                    </div>
                    <div style={{ height: 400, overflow: 'auto' }} onScroll={handleUserScroll}>
                        <List
                            dataSource={userSearchResults}
                            loading={userLoading}
                            renderItem={user => (
                                <List.Item
                                    key={user.id}
                                    onClick={() => handleUserSelect(user)}
                                    style={{ 
                                        cursor: 'pointer',
                                        backgroundColor: selectedUsers.some(u => u.id === user.id) ? '#f0f0f0' : 'transparent'
                                    }}
                                    actions={[
                                        <Checkbox
                                            checked={selectedUsers.some(u => u.id === user.id)}
                                            onClick={e => e.stopPropagation()}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                handleUserSelect(user);
                                            }}
                                        />
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar src={user.avatar} />}
                                        title={<span>{user.name} <Tag color={user.gender === '男' ? 'blue' : 'pink'}>{user.gender}</Tag></span>}
                                        description={
                                            <div>
                                                <div>{user.phone}</div>
                                                {user.dob && <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.45)' }}>出生日期：{user.dob}</div>}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                        {userLoadingMore && (
                            <div style={{ textAlign: 'center', margin: '12px 0' }}>
                                <Spin />
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>已选择 {selectedUsers.length} 个用户</span>
                        <div>
                            <Button 
                                type="link" 
                                onClick={() => setSelectedUsers(userSearchResults)}
                                disabled={!userSearchResults.length}
                            >
                                全选当前页
                            </Button>
                            <Button 
                                type="link" 
                                onClick={() => setSelectedUsers([])}
                                disabled={!selectedUsers.length}
                            >
                                清空选择
                            </Button>
                        </div>
                    </div>
                    <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                        <div style={{ marginBottom: 8 }}>任务日期：</div>
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder="选择任务日期（不选则默认今天）"
                            onChange={(date) => setAssignDate(date ? date.format('YYYY-MM-DD') : null)}
                        />
                    </div>
                </Modal>

                <div style={{ marginBottom: 16 }}>
                    <Select
                        placeholder="选择积分类型"
                        allowClear
                        style={{ width: 200 }}
                        onChange={handlePointTypeChange}
                    >
                        <Option value="reward">积分任务</Option>
                        <Option value="consume">积分活动</Option>
                    </Select>
                </div>
                <Tabs defaultActiveKey="1">
                    <TabPane tab="未归档列表" key="1">
                        <Table
                            loading={loading}
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={activeList}
                            rowKey="_id"
                            scroll={{ x: 1500 }}
                            pagination={{
                                ...pagination,
                                showQuickJumper: true,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 条记录`,
                            }}
                            onChange={handleTableChange}
                        />
                    </TabPane>
                    <TabPane tab="已归档列表" key="2">
                        <Table
                            loading={loading}
                            columns={columns}
                            dataSource={archivedList}
                            rowKey="_id"
                            scroll={{ x: 1500 }}
                            pagination={{
                                ...archivedPagination,
                                showQuickJumper: true,
                                showSizeChanger: true,
                                showTotal: (total) => `共 ${total} 条记录`,
                            }}
                            onChange={(pagination, filters, sorter) => handleTableChange(pagination, filters, sorter, true)}
                        />
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
};

export default Form.create()(PointActivity); 